
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getMockAuth, buildUrl } from 'utils';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { SERVER } from 'constants';
import { API_ENDPOINTS } from 'constants';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthHeader } from 'utils';
const imageCache = new Map(); 

export const useLoadImageWithAuth = () => {
    const { getHttpHeaders } = useAuthHeader();

    const loadImageWithAuth = useCallback(async (fileName) => {
        const imageUrl = `${SERVER}${fileName}`;
        try {
            const headers = await getHttpHeaders();
            const response = await fetch(imageUrl, { headers });
            const blob = await response.blob();
            return URL.createObjectURL(blob); // Return the blob URL to be used in an img src
        } catch (err) {
            console.error('Error fetching the image:', err);
            return null;
        }
    }, [getHttpHeaders]);

    return loadImageWithAuth;
};

export const refreshBlobUrls = async (container, loadImageWithAuth) => {
    const images = container.querySelectorAll('img');

    for (const img of images) {
        const backendId = img.getAttribute('data-backend-id');
        if (backendId) {
            try {
                if (img.src.startsWith("blob:") && !imageCache.has(backendId)) {
                    URL.revokeObjectURL(img.src);
                }
                if (imageCache.has(backendId)) {
                    img.src = imageCache.get(backendId);
                } else {
                    // Fetch and create a new blob URL
                    console.log(backendId)
                    const blobUrl = await loadImageWithAuth(API_ENDPOINTS.UPLOADS.DOWNLOAD(backendId));
                    if (blobUrl) {
                        imageCache.set(backendId, blobUrl);
                        img.src = blobUrl;
                    }
                }
            } catch (error) {
                console.error(`Failed to refresh image with ID ${backendId}:`, error);
            }
        }
    }
};

export const refreshBlobUrlsEditor = async (content, loadImageWithAuth) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const images = doc.querySelectorAll('img');

    for (const img of images) {
        const backendId = img.getAttribute('data-backend-id');
        if (backendId) {
            try {
                if (img.src.startsWith("blob:") && !imageCache.has(backendId)) {
                    URL.revokeObjectURL(img.src);
                }
                if (imageCache.has(backendId)) {
                    img.src = imageCache.get(backendId);
                } else {
                    // Fetch and create a new blob URL
                    const blobUrl = await loadImageWithAuth(API_ENDPOINTS.UPLOADS.DOWNLOAD(backendId));
                    if (blobUrl) {
                        imageCache.set(backendId, blobUrl);
                        img.src = blobUrl;
                    }
                }
            } catch (error) {
                console.error(`Failed to refresh image with ID ${backendId}:`, error);
            }
        }
    }
    return doc.body.innerHTML;
};

export function useFetchData(endpoint, updateEndpoint = null, callOnInit = true, initialQueryParams = null, callAuthenticated = true, updateOnParamChange = true) {
    const { isAuthenticated:isAuth0Authenticated, isLoading:isAuth0Loading } = useAuth0();
    const { getHttpHeaders } = useAuthHeader();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const debouncedChangesRef = useRef({});
    //let debouncedChanges = {};
    let debouncedLineChanges = {};

    const { isMockEnabled } = getMockAuth()
    const [isLoadingAuth0, setIsLoadingAuth0] = useState(isAuth0Loading && !isMockEnabled)
    const [isAuthenticated, setIsAuthenticated] = useState(isAuth0Authenticated || isMockEnabled)

    const [data, setData] = useState(null);
    const [queryParams, setQueryParams] = useState(initialQueryParams);
    const [isLoading, setIsLoading] = useState(callOnInit);
    const [isUpdating, setIsUpdating] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [updateError, setUpdateError] = useState(null);
    const [updateSuccess, setUpdateSuccess] = useState(null);
    const [isDeleting, setIsDeleting] = useState(null);
    const [delteSuccess, setDeleteSuccess] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [mediaType, setMediaType] = useState('');

    if(isMockEnabled)
        console.log(`Is mock enabled ${isMockEnabled}`)

    useEffect(() => {
        setIsLoadingAuth0(isAuth0Loading && !isMockEnabled)
    }, [isAuth0Loading, isMockEnabled]);

    useEffect(() => {
        setIsAuthenticated(isAuth0Authenticated || isMockEnabled)
    }, [isAuth0Authenticated, isMockEnabled]);

    useEffect(() => {
        if(endpoint && !isLoadingAuth0 && (isAuthenticated || !callAuthenticated) && callOnInit){
            if(queryParams){
                fetchData(queryParams);
            } else fetchData();
        }
    }, [isLoadingAuth0, isAuthenticated, callOnInit, queryParams]);

    useEffect(() => {
        if(updateOnParamChange){
            const initialParamsStr = JSON.stringify(initialQueryParams);
            const currentParamsStr = JSON.stringify(queryParams);

            if (initialParamsStr !== currentParamsStr) {
                setQueryParams(initialQueryParams);
            }
        }
    }, [initialQueryParams]);
    
    const makeApiCall = async (endpoint, payload = {}, item = null, params = null, setError=setSaveError, method = 'POST', tenantId) => {
        const controller = new AbortController();
        const { signal } = controller;
        console.log('makeApiCall', endpoint)

        try {
            const headers = callAuthenticated ? await getHttpHeaders(tenantId) : undefined;
            const queryString = buildUrl(SERVER, endpoint, params, item);

            const config = { headers, signal, timeout: 30000 };

            if (method === 'GET_BLOB') {
                config.responseType = 'arraybuffer';
            }

            let response;
            switch (method) {
                case 'POST':
                    response = await axios.post(queryString, payload, config);
                    break;
                case 'GET':
                case 'GET_BLOB':
                    response = await axios.get(queryString, config);
                    break;
                case 'DELETE':
                    if(payload){
                        config.headers['Content-Type'] = 'application/json'
                        config.data = payload
                    }
                    response = await axios.delete(queryString, config);
                    break;
                case 'PATCH':
                    response = await axios.patch(queryString, payload, config);
                    break;
                default:
                    throw new Error(`Unsupported method: ${method}`);
            }

            if(process.env.REACT_APP_ENVIRONMENT_STAGE === 'DEV')
                console.log("API CALL", payload, queryString, method, response.data);

            return response
        } catch (error) {
            console.error('Error during API call:', error);

            if (error.response?.status === 403) {
                console.warn("403 Forbidden detected. Redirecting to tenant selection...");
                navigate("/select-tenant");
            }

            setError(error);
            return null;
        }
    };

    const fetchBlob = useCallback(async () => {
        setIsLoading(true);
        setError(null)

        const response = await makeApiCall(endpoint, null, null, queryParams, setError, 'GET_BLOB');
        if (response) {
            const contentType = response.headers['content-type'];
            const pdfBlob = new Blob([response.data], { type: contentType });
            setIsLoading(false);
            setMediaType(contentType)

            return pdfBlob

            //return URL.createObjectURL(pdfBlob);
        } else {
            setIsLoading(false);
            return null;
        }
    }, [endpoint]);

    const fetchData = useCallback(async (newParams = null, tenantId) => {
        setIsLoading(true);
        setError(null)

        const response = await makeApiCall(endpoint, null, null, newParams ? newParams : queryParams, setError, 'GET', tenantId);
        const responseData = response?.data
        if (responseData) {
            setData(responseData);
        }
        setIsLoading(false);
        return responseData;
    }, [endpoint]);

    const saveData = async (param = null, urlParam = null) => {
        //console.log('saveData')
        setSuccess(null)
        setSaveError(null)
        if(!param)param = data
        if(!param){console.log('Tried to save no data');return}

        const response = await makeApiCall(endpoint, param, null, urlParam, setSaveError)
        const responseData = response?.data
        if(responseData){
            setSuccess({status: true, message: t('alert.saved'), response: responseData})
        } 
        return responseData;
    };

    const triggerUpdate = async (item = null, payload = null, endpoint = null) => {

        setUpdateError(null)
        setUpdateSuccess(null)
        setIsUpdating(true)

        const response = await makeApiCall(endpoint || updateEndpoint, payload, item, null, setUpdateError)
        const responseData = response?.data

        if(responseData){
            setUpdateSuccess({status: true, message: responseData})
        } 
        setIsUpdating(false);
        return responseData;
    }

    const triggerDelete = async (payload = null, endpoint = null) => {
        setDeleteError(null)
        setDeleteSuccess(null)
        setIsDeleting(true)

        const response = await makeApiCall(endpoint || updateEndpoint, payload, null, null, setDeleteError, 'DELETE')
        const responseData = response?.data

        if(responseData){
            setDeleteSuccess({status: true, message: responseData})
        } 
        setIsDeleting(false);
        return responseData;
    }

    //const debounceSaveData = debounce(saveData, 300)

    const update = async (item = null, payload = null) => {
        await triggerUpdate(item, payload)
        fetchData();
    }

    const updateWithResponse = async (item = null, payload = null) => {
        const result = await triggerUpdate(item, payload)
        if(result){
            setData(result)
        }
    }

    const setAndSaveUniqueFlag = (id, key) => {
        const updatedData = data?.map((item) => ({ ...item, [key]: item.id === id }));
        setData(updatedData);
        saveData(updatedData.map((item) => ({ id: item.id, [key]: item[key] })));
    };

    const setAndSave = (newData) => {
        setData(newData)
        saveData(newData)
    }

    const setSaveResponse = async (data) => {
        setData(data)
        return await saveData(data)
    }

    const addLine = async (newLine) => {
        const newData = await saveData([...data, newLine])
        setData(newData)
        return newData
    }

    const debounceSaveLineData = debounce(() => {
        if (Object.keys(debouncedLineChanges).length > 0) {
            const changedDataArray = Object.keys(debouncedLineChanges).map((id) => {
                return { ...debouncedLineChanges[id], id };
            });
            saveData(changedDataArray);
            debouncedLineChanges = {}; 
        }
    }, 300);

    const setLineKey = (id, key, value, idField = 'id') => {
        const updatedData = data.map(line => line[idField] === id ? { ...line, [key]: value } : line);
        setData(updatedData);
    }
    
    const saveLineKey = async (id, key, value, idField = 'id', debounce = false) => {
        console.log(id, key, value)
        const changedData = { [key]: value };

        if (debounce) {
            debouncedLineChanges[id] = { ...debouncedLineChanges[id], ...changedData };
            setData(prevData => prevData.map(line => {
                const updatedLine = debouncedLineChanges[line[idField]];
                return updatedLine ? { ...line, ...updatedLine } : line;
            }));
            
            debounceSaveLineData();
        } else {
            const updatedData = data.map(line => line[idField] === id ? { ...line, [key]: value } : line);
            setData(updatedData);
            const updatedLine = updatedData.find(line => line[idField] === id);
            if (updatedLine) {
                saveData([{ ...changedData, id }]);
            }
        }
    };

    const toggleLineKey = async (id, key) => {
        setAndSave(data.map(line => line.id === id ? { ...line, [key]: !line[key] } : line));
    }

    const debounceSaveData = useCallback(debounce(async () => {
        //console.log('debounced save: ', debouncedChangesRef.current)
        const savedData = saveData(debouncedChangesRef.current);
        //const savedData = saveData({ ...debouncedChangesRef.current, id: data.id });
        //console.log(debouncedChangesRef.current)
        debouncedChangesRef.current = {}; 
    }, 300), []);

    const saveKey = async (key, value, debounce=true) => {
        //console.log(`saveKey: ${key}, value: ${value}, debounce:`, debounce, endpoint)
        const changedData = { [key]: value, id: data.id };
        //debouncedChangesRef.current = { ...debouncedChangesRef.current, ...changedData };

        if(debounce) {
            debouncedChangesRef.current = { ...debouncedChangesRef.current, ...changedData };
            //console.log('to be sent', debouncedChangesRef.current)
            debounceSaveData()
            setData((prevData) => ({
                ...prevData,
                ...changedData,
            }));
        }
        else {
            const savedData = await saveData(changedData)
            if(savedData){
                setData(savedData)
            } else {
                setData((prevData) => ({
                    ...prevData,
                    ...changedData,
                }));
            }
        }
    }

    const deleteItem = (id) => {
        saveData(data.map(item => item.id === id ? { ...item, is_deleted: true } : item))
        setData(prevData => prevData.filter(item => item.id !== id));

        //saveData(data.map(item => item.id === id ? { ...item, is_deleted: true } : item))
        //setData(data.filter(item => item.id !== id));
    };

    return { 
        makeApiCall,
        data, setData, isLoading, 
        error, setError, 
        saveError, setSaveError, 
        triggerDelete, isDeleting, delteSuccess, deleteError,
        update, triggerUpdate, updateWithResponse, isUpdating, updateError, setUpdateError, updateSuccess, setUpdateSuccess,
        success, saveData, debounceSaveData,
        setLineKey, saveLineKey, setAndSaveUniqueFlag, saveKey, setAndSave, refetch: fetchData, fetchBlob, mediaType,
        setIsUpdating, queryParams, setQueryParams, deleteItem, isAuthenticated, isLoadingAuth0, addLine, toggleLineKey, setSaveResponse};
}
